// Core packages
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Third party packages
import { MatDialog } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

// Custom packages
import { ToastrService } from 'ngx-toastr';
import { DisclaimerDialogComponent } from 'src/app/components/disclaimer-dialog/disclaimer-dialog.component';
import { PrivacyDialogComponent } from "src/app/components/privacy-dialog/privacy-dialog.component";
import { AccessLogsService } from 'src/app/modules/access-logs/access-logs.service';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { sharedUserLanding } from 'src/app/modules/properties/document-archive-entries.utils';
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from '../../services/helper.service';
import { ListsService } from '../../services/lists.service';

@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss']
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  // tslint:disable-next-line:no-input-rename
  @Input('page-title') pageTitle: string;
  @Input('itemName') itemName: string;
  @Input('propertyId') propertyId?: string;
  @Input() isPropertyHome?: boolean;
  @Input('hideList') hideList?: boolean;
  chosenRoleId: string;
  userRoles: any[];
  // Filtered + sorted view of userRoles rendered inside the select panel.
  // Source data comes from auth/check (loggedUser$): the whole pipeline is client-side.
  displayedUserRoles: any[] = [];
  // Search filters (text, case-insensitive, applied together as AND).
  roleTypeSearch = '';
  companySearch = '';
  // Independent multi-key sort directions: role type is the primary key, company the secondary.
  // Each cycles through '' (none) -> 'asc' -> 'desc'.
  roleTypeSortDir: '' | 'asc' | 'desc' = '';
  companySortDir: '' | 'asc' | 'desc' = '';

  lang: string;
  loggedUser: IUser;

  disclaimer: any = {};

  /**
   * Class constructor
   */
  constructor(
    private authService: AuthService,
    private cookieService: CookieService,
    private router: Router,
    private configService: ConfigService,
    private translateService: TranslateService,
    private accessLogsService: AccessLogsService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    private listsService: ListsService,
    public companiesService: CompaniesService,
    private dialog: MatDialog,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Subscribe to logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(
        (user: any) => {
          this.loggedUser = user;
          if (user && user.userRoles) {
            this.userRoles = user.userRoles;
          } else {
            this.userRoles = [];
          }
          this.applyRoleFilters();
        }
      )
    );

    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleObj$.subscribe(
        res => {
          this.chosenRoleId = res._id;
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle current role change event and inform authService
   * that the current requested role has changed
   *
   * @since 1.0.0
   */
  onRoleChange(): void {
    this.authService.isRoleChanging$.next(true);

    const chosenRole = this.userRoles.find(el => el._id === this.chosenRoleId);
    const companyId = chosenRole.company ? chosenRole.company._id : undefined;

    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    const privacy = chosenRole.privacy;
    if (chosenRole.role.type === "temporary" && (typeof privacy === 'undefined' || !privacy)) {
      const dialogRef = this.dialog.open(
        PrivacyDialogComponent,
        {
          data: {
            userId: this.loggedUser._id,
            roleId: this.chosenRoleId
          },
          width: "800px",
          disableClose: true,
        }
      );
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) {
            this.authService.logout(true);
          }
        });
      // return;
    }

    if (companyId) {
      this.subscriptions.push(
        this.companiesService
          .get(companyId)
          .subscribe(
            (companyRes) => {
              if (companyRes.data && companyRes.data.disclaimer) {
                this.disclaimer = companyRes.data.disclaimer;
              }
              if (
                companyRes.status &&
                companyRes.data &&
                (
                  !companyRes.data.disclaimer ||
                  !companyRes.data.disclaimer.check1 ||
                  !companyRes.data.disclaimer.check2 ||
                  !companyRes.data.disclaimer.check3 ||
                  !companyRes.data.disclaimer.check4
                )
              ) {
                const loggedUserId = this.loggedUser._id.toString();
                if (
                  // this.loggedUser._id === '5d974025f0cd540fa4bd2d48' || // SITI
                  // this.loggedUser._id === '5f85adf518e3cf63c87933d9' || // SITI fabio
                  // this.loggedUser._id === '60c8586211ab7c61927de461' || // cinzia.archedi@gmail.com
                  // // this.loggedUser._id === '5f7dd2d8172e822bd0477be6' || // daniele.spasaro@archedi.it
                  // // this.loggedUser._id === '5f7dd2d8172e822bd0477ba0' || // daniele.spasaro@gmail.com
                  // // this.loggedUser._id === '5f7dd2d8172e822bd0477c99' || // daniele.spasaro@studioassociatogamma.com
                  // this.loggedUser._id === '5ff8154d6e0b8d388c7d8fc2' || // moseva2013@gmail.com
                  // this.loggedUser._id === '5ef210e3e5da4742563f4c75' || // info@archedi.it
                  // this.loggedUser._id === '60631f3166a6a849c4462d20' || // segreteria@archedi.it
                  // this.loggedUser._id === '5f7dd2d8172e822bd0477bd3' || // vanessa.c.archedi@gmail.com
                  // this.loggedUser._id === '6176a4df0ab08f064e35244f' || // vanessa.cicognani@studioassociatogamma.com
                  // this.loggedUser._id === '5f7dd2d8172e822bd0477e44' || // cinzia.ragusag@gmail.com
                  // this.loggedUser._id === '6176a5190ab08f064e352452' || //cinzia.ragusa@studioassociatogamma.com
                  // this.loggedUser._id === '5f7dd2d8172e822bd0477e44'    // cinzia.ragusag@gmail.com

                  loggedUserId !== '5d974025f0cd540fa4bd2d48' && // info@sitisrl.it
                  loggedUserId !== '5f7dd2d8172e822bd0477b92' && // archedi@archedi.it
                  loggedUserId !== '5f7dd2d8172e822bd0477be6' && // daniele.spasaro@archedi.it
                  loggedUserId !== '5f86fb7bf06c117f1a0d5875' && // francescobancalari@gmail.com
                  loggedUserId !== '5ff8154d6e0b8d388c7d8fc2' && // moseva2013@gmail.com
                  loggedUserId !== '60acf503c3fac95703a36d8e' && // luca.sarti@oneam.it
                  loggedUserId !== '60c8586211ab7c61927de461' && // cinzia.archedi@gmail.com
                  loggedUserId !== '613a1237db63672bc5444677' && // rboccasso@gmail.com
                  loggedUserId !== '5ef210e3e5da4742563f4c75' && // info@archedi.it
                  loggedUserId !== '5f7dd2d8172e822bd0477bd7' && // laura.s.archedi@gmail.com
                  loggedUserId !== '5f7dd2d8172e822bd0477c6c' && // info@asgsrl.com
                  loggedUserId !== '5f7dd2d8172e822bd0477d2f' && // paolofrigieri@icloud.com
                  loggedUserId !== '5f7dd2d8172e822bd0477d37' && // paolasantambrogio@tin.it
                  loggedUserId !== '5f7dd2d8172e822bd0477e48' && // info@studioassociatogamma.com
                  loggedUserId !== '5f7dd2d9172e822bd0478018' && // anna.garbelli@gmail.com
                  loggedUserId !== '5f85adf518e3cf63c87933d9' && // fabio.stangalini@sitisrl.it
                  loggedUserId !== '60f0467bda984c5931b22d09' && // m.coti@studioassociatogamma.com
                  loggedUserId !== '6139cd12db63672bc54411f8' && // gianlorenzo.zardini@gmail.com
                  loggedUserId !== '61404eb4db63672bc54e027c' && // scilla.ioppolo@tiscali.it
                  loggedUserId !== '6143472f6dc6100ff790dc31' && // marco.deangeli@studioassociatogamma.com
                  loggedUserId !== '61f7df04188a702dec5610bd' && // nicolo.felice@studioassociatogamma.com
                  loggedUserId !== '6267c3363d13ce3a6a78918f' && // annalisa.mannucci@studioassociatogamma.com
                  loggedUserId !== '629da9fd7003cc64a43fd80f' && // masamatteo45@gmail.com
                  loggedUserId !== '63fc7cc75ef4cf243c55be32' && // irenemanzoni@gmail.com
                  loggedUserId !== '641071673ec7974b112d4a77' && // silviadonato10@gmail.com
                  loggedUserId !== '648704bbad4b1e55999d09eb' && // sil.lai.lucia@gmail.com
                  loggedUserId !== '649aa862d981314352bd0f24' && // marcomari@pegasopc.it
                  loggedUserId !== '649aa8b4d981314352bd0f26' && // mattiabarbetta.mb@gmail.com
                  loggedUserId !== '64a7ecc468d7703d3b7e62e4' && // c.cacopardi@gmail.com
                  loggedUserId !== '6501d1ed57eaad6f3cdf079c' && // arch.margherita.corsi@gmail.com
                  loggedUserId !== '653bcab99861152e10496db7' // margherita.corsi@archedi.it
                ) {
                  if (chosenRole.type === "master") {
                    const dialogRef = this.dialog.open(
                      DisclaimerDialogComponent,
                      {
                        data: {
                          companyId,
                        },
                        width: "800px",
                        disableClose: true,
                      }
                    );
                    dialogRef
                      .beforeClosed()
                      .pipe(take(1))
                      .subscribe((res) => {
                        if (!res) {
                          this.authService.logout(true);
                        }
                      });
                    return;
                  } else if (chosenRole.type === "collaborator") {
                    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                      data: {
                        title: this.translateService.instant("dialogs.disclaimer.title"),
                        message: this.translateService.instant(
                          "dialogs.disclaimer.messageToCollaborator"
                        ),
                        btnOkText: this.translateService.instant("common.cta.close"),
                        btnCancelText: this.translateService.instant("common.cta.close"),
                        messageOnly: true,
                      },
                      width: "500px",
                      disableClose: true,
                    });
                    dialogRef
                      .beforeClosed()
                      .pipe(take(1))
                      .subscribe(() => {
                        this.authService.logout(true);
                      });
                    return;
                  }
                }
              }
              return;

            },
            (err) => {
              this.helperService.handleError(err);
            }
          )
      );
    }

    // Reset lists service data
    this.listsService.resetAllSettings();
    // Update req role and access log
    this.authService.setRequestRoleId(this.chosenRoleId, chosenRole.role._id);
    this.accessLogsService.create(chosenRole.type, companyId).subscribe(
      res => {
        if (res) {
          this.authService.isRoleChanging$.next(false);
          this.toastrService.success(this.translateService.instant("accessLogs.createdSuccessfully"));
          if (this.authService.loggedUserRequestedRoleType$.getValue() === "shared") {
            const lang = this.translateService.currentLang || this.translateService.defaultLang;
            // my-property until the switch, the new archive of the role's property after it
            this.router.navigate(sharedUserLanding(lang, chosenRole));
          } else if (this.authService.loggedUserRequestedRoleType$.getValue() === "temporary") {
            const lang = this.translateService.currentLang || this.translateService.defaultLang;
            this.router.navigate([lang, 'app', 'properties', 'list']);
          } else {
            this.router.navigate([this.lang, 'app'], { replaceUrl: true });
          }
        }
      },
      err => {
        this.helperService.handleError(err);
      }
    );
  }

  /**
   * Resolve the translated label of a role's type, matching exactly what is
   * displayed inside the select option (e.g. "Master", "Collaboratore").
   *
   * @since 3.0.0
   */
  getRoleTypeLabel(element: any): string {
    if (!element || !element.role) {
      return '';
    }
    const type = element.role.type
      ? element.role.type
      : (element.role.name ? element.role.name.toLowerCase() : '');
    if (!type) {
      return '';
    }
    return this.translateService.instant('roles.types.' + type);
  }

  /**
   * Resolve the company name bound to a role (empty string when absent).
   *
   * @since 3.0.0
   */
  getCompanyName(element: any): string {
    return element && element.company && element.company.name
      ? element.company.name
      : '';
  }

  /**
   * Full label shown in the select trigger ("<role type> - <company>").
   * Kept decoupled from the filtered option list so the trigger never goes
   * blank when the current role is filtered out.
   *
   * @since 3.0.0
   */
  getRoleDisplayLabel(element: any): string {
    if (!element) {
      return '';
    }
    const typeLabel = this.getRoleTypeLabel(element);
    const company = this.getCompanyName(element);
    return company ? typeLabel + ' - ' + company : typeLabel;
  }

  /**
   * Return the currently selected role object from the full (unfiltered) list.
   *
   * @since 3.0.0
   */
  getChosenRole(): any {
    if (!this.userRoles || !this.chosenRoleId) {
      return null;
    }
    return this.userRoles.find(el => el._id === this.chosenRoleId);
  }

  /**
   * FontAwesome icon reflecting a sort direction.
   *
   * @since 3.0.0
   */
  getSortIcon(direction: string): string {
    if (direction === 'asc') {
      return 'fa-arrow-up';
    }
    if (direction === 'desc') {
      return 'fa-arrow-down';
    }
    return 'fa-sort';
  }

  /**
   * Cycle a sort direction through '' (none) -> 'asc' -> 'desc' -> ''.
   *
   * @since 3.0.0
   */
  private nextSortDirection(current: string): '' | 'asc' | 'desc' {
    if (current === 'asc') {
      return 'desc';
    }
    if (current === 'desc') {
      return '';
    }
    return 'asc';
  }

  /**
   * Toggle the role-type sort and recompute the displayed list.
   *
   * @since 3.0.0
   */
  toggleRoleTypeSort(): void {
    this.roleTypeSortDir = this.nextSortDirection(this.roleTypeSortDir);
    this.applyRoleFilters();
  }

  /**
   * Toggle the company sort and recompute the displayed list.
   *
   * @since 3.0.0
   */
  toggleCompanySort(): void {
    this.companySortDir = this.nextSortDirection(this.companySortDir);
    this.applyRoleFilters();
  }

  /**
   * Recompute displayedUserRoles applying both text filters (role type label +
   * company name, combined as AND) and the two independent sorts (role type
   * primary, company secondary). Purely client-side over loggedUser$ data.
   *
   * @since 3.0.0
   */
  applyRoleFilters(): void {
    let list = (this.userRoles || []).slice();

    const typeQuery = this.roleTypeSearch.trim().toLowerCase();
    if (typeQuery) {
      list = list.filter(
        el => this.getRoleTypeLabel(el).toLowerCase().indexOf(typeQuery) >= 0
      );
    }

    const companyQuery = this.companySearch.trim().toLowerCase();
    if (companyQuery) {
      list = list.filter(
        el => this.getCompanyName(el).toLowerCase().indexOf(companyQuery) >= 0
      );
    }

    if (this.roleTypeSortDir || this.companySortDir) {
      list.sort((a, b) => {
        if (this.roleTypeSortDir) {
          const cmp = this.getRoleTypeLabel(a).toLowerCase()
            .localeCompare(this.getRoleTypeLabel(b).toLowerCase());
          if (cmp !== 0) {
            return this.roleTypeSortDir === 'asc' ? cmp : -cmp;
          }
        }
        if (this.companySortDir) {
          const cmp = this.getCompanyName(a).toLowerCase()
            .localeCompare(this.getCompanyName(b).toLowerCase());
          if (cmp !== 0) {
            return this.companySortDir === 'asc' ? cmp : -cmp;
          }
        }
        return 0;
      });
    }

    this.displayedUserRoles = list;
  }
}
